// ============================================================================
// ADR-0024 N-3 — Related Party Register helpers
// ============================================================================
// Mirrors the server's RelatedPartyRegister (Describe) so the editor shows the
// "Particulars" exactly as the note will print them.

import type {
  RelatedEntityType,
  RelatedParty,
  RelatedPartyCategory,
  RelatedPartyLinkRole,
  RelatedPartyTransaction,
} from 'api/types/fsa/corporateFacts.types';

export const CATEGORY_OPTIONS: Array<{ value: RelatedPartyCategory; label: string; person: boolean }> = [
  { value: 'KMP', label: 'Key Management Personnel (KMP)', person: true },
  { value: 'RELATIVE_OF_KMP', label: 'Relative of KMP', person: true },
  { value: 'ENTITY_KMP', label: 'Enterprise owned / significantly influenced by KMP or relatives', person: false },
  { value: 'HOLDING', label: 'Holding company', person: false },
  { value: 'SUBSIDIARY', label: 'Subsidiary', person: false },
  { value: 'FELLOW_SUBSIDIARY', label: 'Fellow subsidiary', person: false },
  { value: 'ASSOCIATE', label: 'Associate', person: false },
  { value: 'JOINT_VENTURE', label: 'Joint venture', person: false },
  { value: 'OTHER', label: 'Other related party', person: false },
];

export const ENTITY_TYPE_OPTIONS: Array<{ value: RelatedEntityType; label: string; noun: string }> = [
  { value: 'COMPANY', label: 'Company', noun: 'Company' },
  { value: 'LLP', label: 'LLP', noun: 'LLP' },
  { value: 'FIRM', label: 'Partnership firm', noun: 'Firm' },
  { value: 'PROPRIETORSHIP', label: 'Proprietorship', noun: 'Proprietorship concern' },
  { value: 'TRUST', label: 'Trust', noun: 'Trust' },
  { value: 'HUF', label: 'HUF', noun: 'HUF' },
  { value: 'OTHER', label: 'Other', noun: 'Enterprise' },
];

export const LINK_ROLE_OPTIONS: Array<{ value: RelatedPartyLinkRole; label: string }> = [
  { value: 'DIRECTOR', label: 'Director' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'PROPRIETOR', label: 'Proprietor' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'TRUSTEE', label: 'Trustee' },
  { value: 'KARTA', label: 'Karta' },
  { value: 'SHAREHOLDER', label: 'Substantial interest' },
];

/** Transaction natures offered in the editor (free text is allowed). */
export const NATURE_SUGGESTIONS = [
  'Remuneration paid', 'Sitting fees paid', 'Commission paid', 'Sale of goods / services', 'Purchase of goods / services',
  'Rent paid', 'Rent received', 'Loan taken', 'Loan repaid', 'Loan given', 'Loan received back', 'Interest paid',
  'Interest received', 'Reimbursement of expenses', 'Advance given', 'Guarantee given', 'Equity shares issued',
];

/** Natures that are AS 18 only by default (not a s.188(1) contract for Form AOC-2). */
export function defaultInAoc2(nature: string | undefined): boolean {
  const n = (nature ?? '').toLowerCase();
  return !(n.includes('remuneration') || n.includes('sitting fee') || n.includes('salary'));
}

export const isPersonCategory = (c: RelatedPartyCategory) => c === 'KMP' || c === 'RELATIVE_OF_KMP';

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "Particulars" as printed (mirrors RelatedPartyRegister.Describe). */
export function describeParty(p: RelatedParty, byId: Map<string, RelatedParty>): string {
  if (p.details?.trim()) return p.details.trim();
  if (p.category === 'KMP') return p.designation?.trim() ?? '';
  if (p.category === 'RELATIVE_OF_KMP') {
    const of = p.relative_of ? byId.get(p.relative_of)?.name : undefined;
    const relation = p.relation?.trim() || 'Relative';
    return of ? `${relation} of ${of}` : relation;
  }
  const links = p.links ?? [];
  if (links.length === 0) return '';
  const noun = ENTITY_TYPE_OPTIONS.find(o => o.value === p.entity_type)?.noun ?? 'Enterprise';
  const roles: RelatedPartyLinkRole[] = [];
  for (const l of links) if (!roles.includes(l.role)) roles.push(l.role);
  const parts: string[] = [];
  for (const role of roles) {
    const names = links.filter(l => l.role === role).map(l => byId.get(l.party_id)?.name).filter((n): n is string => !!n);
    if (names.length === 0) continue;
    const who = joinNames(names);
    const many = names.length > 1;
    parts.push(
      role === 'PROPRIETOR' ? `of ${who}`
        : role === 'DIRECTOR' ? `in which ${who} ${many ? 'are directors' : 'is a director'}`
          : role === 'PARTNER' ? `in which ${who} ${many ? 'are partners' : 'is a partner'}`
            : role === 'MEMBER' ? `in which ${who} ${many ? 'are members' : 'is a member'}`
              : role === 'TRUSTEE' ? `in which ${who} ${many ? 'are trustees' : 'is a trustee'}`
                : role === 'KARTA' ? `of which ${who} ${many ? 'are the karta' : 'is the karta'}`
                  : `in which ${who} ${many ? 'have' : 'has'} a substantial interest`,
    );
  }
  return parts.length === 0 ? '' : `${noun} ${parts.join('; and ')}`;
}

/** Party ids something else still points at (a party that is referenced cannot be removed). */
export function referencedPartyIds(parties: RelatedParty[], txns: RelatedPartyTransaction[]): Set<string> {
  const ids = new Set<string>();
  for (const t of txns) if (t.party_id) ids.add(t.party_id);
  for (const p of parties) {
    if (p.relative_of) ids.add(p.relative_of);
    for (const l of p.links ?? []) ids.add(l.party_id);
  }
  return ids;
}
